import { createFileRoute } from "@tanstack/react-router";
import { Mail, MapPin } from "lucide-react";

import { LegalHomeButton } from "@/components/legal-home-button";
import { siteInfo } from "@/lib/site-info";

export const Route = createFileRoute("/impressum")({
  staticData: { sitemap: true },
  head: () => ({
    meta: [
      { title: "Impressum. Begner" },
      {
        name: "description",
        content: `Impressum und rechtliche Hinweise der ${siteInfo.legalName} in ${siteInfo.address.addressLocality}.`,
      },
    ],
  }),
  component: LegalNoticePage,
});

const legalSections = [
  {
    title: "Haftungsausschluss",
    text: "Die Inhalte dieser Website werden mit grösstmöglicher Sorgfalt erstellt und laufend weiterentwickelt. Dennoch übernimmt die SEO Begani keine Gewähr für die Richtigkeit, Vollständigkeit, Aktualität oder jederzeitige Verfügbarkeit der bereitgestellten Informationen. Dies gilt insbesondere für Angaben in Auftragnehmerprofilen, Aufträgen, Bewertungen und Immobilieninseraten, die ganz oder teilweise von Nutzerinnen, Nutzern oder Dritten stammen.",
  },
  {
    title: "Richtpreise und Angebote",
    text: "Die im Kostenrechner und in redaktionellen Inhalten genannten Preise und Preisspannen sind unverbindliche Richtwerte. Sie stellen weder eine Offerte noch eine Preisgarantie dar. Verbindliche Preise und Leistungen werden ausschliesslich zwischen den jeweiligen Kundinnen oder Kunden und den ausgewählten Auftragnehmern beziehungsweise Immobilienanbietern vereinbart.",
  },
  {
    title: "Vermittlung über SEO Begani",
    text: "SEO Begani stellt eine Plattform zur Veröffentlichung und Vermittlung von Aufträgen, Anbieterprofilen und Immobilieninseraten bereit. Soweit nicht ausdrücklich anders angegeben, wird die SEO Begani nicht Partei eines zwischen Nutzerinnen, Nutzern und Anbietern geschlossenen Vertrags. Für die Ausführung, Qualität, Termine, Preise und Abwicklung vereinbarter Leistungen sind die jeweiligen Vertragsparteien verantwortlich.",
  },
  {
    title: "Haftung für Links",
    text: "Verweise und Links auf Websites Dritter liegen ausserhalb unseres Verantwortungsbereichs. Der Zugriff und die Nutzung solcher Websites erfolgen auf eigene Gefahr.",
  },
  {
    title: "Urheberrecht",
    text: "Die Inhalte und Werke auf dieser Website unterliegen dem schweizerischen Urheberrecht. Jede Verwertung ausserhalb der gesetzlichen Grenzen bedarf der vorherigen schriftlichen Zustimmung des jeweiligen Rechteinhabers.",
  },
] as const;

function LegalNoticePage() {
  return (
    <main>
      <section className="border-b border-border/70 bg-surface">
        <div className="container-page py-14 sm:py-20">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-deep">
            Rechtliches
          </p>
          <h1 className="mt-4 font-display text-4xl font-extrabold tracking-tight sm:text-5xl">
            Impressum
          </h1>
          <p className="mt-4 max-w-2xl text-lg leading-8 text-muted-foreground">
            Anbieterkennzeichnung und rechtliche Hinweise zu dieser Website.
          </p>
          <div className="mt-7">
            <LegalHomeButton />
          </div>
        </div>
      </section>

      <section className="container-page py-12 sm:py-16">
        <div className="grid gap-10 lg:grid-cols-[320px_minmax(0,1fr)] lg:gap-16">
          <aside
            id="kontakt"
            className="h-fit scroll-mt-24 rounded-3xl border border-border bg-card p-6 shadow-card sm:p-8 lg:sticky lg:top-24"
          >
            <h2 className="font-display text-2xl font-bold">{siteInfo.legalName}</h2>
            <address className="mt-6 space-y-5 text-sm not-italic">
              <p className="flex items-start gap-3 leading-6 text-muted-foreground">
                <MapPin className="mt-0.5 size-5 shrink-0 text-brand-deep" aria-hidden />
                <span>
                  {siteInfo.address.streetAddress}
                  <br />
                  {siteInfo.address.postalCode} {siteInfo.address.addressLocality}
                  <br />
                  Schweiz
                </span>
              </p>
              <a
                href={`mailto:${siteInfo.email}`}
                className="flex items-center gap-3 font-semibold text-brand-deep underline-offset-4 hover:underline"
              >
                <Mail className="size-5 shrink-0" aria-hidden />
                {siteInfo.email}
              </a>
            </address>
          </aside>

          <div className="divide-y divide-border">
            {legalSections.map((section, index) => (
              <article key={section.title} className={index === 0 ? "pb-10" : "py-10"}>
                <h2 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">
                  {section.title}
                </h2>
                <p className="mt-4 max-w-3xl text-base leading-8 text-muted-foreground">
                  {section.text}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
